// eslint-disable-next-line no-unused-vars
import { ColorX } from "IZOX/STATIC";
import _ from "lodash";
// eslint-disable-next-line no-unused-vars
import React, { CSSProperties } from "react";
import XProtect from "./_gears/XProtect";

/**
 * @typedef {"none" | "dotted" | "inset" | "solid" | "dashed" 
 *  | "groove" | "hidden" | "double" | "ridge" | "outset"} bS
 * 
 * @typedef {Object} xdivparamsAddOns
 * @property {CSSProperties} style
 * @property {any} children
 * @property {Number | String} width
 * @property {Number | String} height
 * @property {Number | String} minWidth
 * @property {Number | String} maxWidth
 * @property {Number | String} minHeight
 * @property {Number | String} maxHeight
 * @property {"row" | "column"} flexDirection
 * @property {"flex-start" | "center" | "flex-end" | "space-between" | "space-around" | "space-evenly" } justifyContent
 * @property {"flex-start" | "center" | "flex-end" | "space-between" | "space-around" | "space-evenly" } alignItems
 * @property {"flex-start" | "center" | "flex-end" | "stretch" | "baseline"} alignSelf
 * @property {Number | [Number, Number, Number, Number]} p
 * @property {Number} pH
 * @property {Number} pV
 * @property {Number} pT
 * @property {Number} pB
 * @property {Number} pL
 * @property {Number} pR
 * @property {Number | [Number, Number, Number, Number]} m
 * @property {Number} mH
 * @property {Number} mV
 * @property {Number} mT
 * @property {Number} mB
 * @property {Number} mL
 * @property {Number} mR
 * @property {Number | [Number, Number, Number, Number]} bW
 * @property {bS | [bS, bS, bS, bS]} bStyle
 * @property {Number | [Number, Number, Number, Number]} round
 * @property {String} color
 * @property {String | [String, String, String, String]} bColor
 * @property {String} bgColor
 * @property {Boolean} shadow
 * @property {Number} shadowSize
 * @property {String} shadowColor
 * @property {Boolean} fullWidth
 * @property {Boolean} fullHeight
 * @property {Boolean} fitWidthToContent
 * @property {Boolean} fitHeightToContent
 * @property {Boolean} autoWidth
 * @property {Boolean} autoHeight
 * @property {Boolean} noPropagation
 * @property {any} forwardRef
 * @property {"absolute" | "relative" | "static" | "fixed" | "sticky"} position
 * @property {"hidden" | "visible" | "scroll" | "auto"} overflow
 * @property {"static" | "block" | "flex" | "inline-block" | "none" | "grid"} display
 * @property {Number} zIndex
 * @property {Boolean} flex
 * @property {Number} flexShrink
 * @property {Number} flexGrow
 * @property {Number | String} flexBasis
 * @property {"nowrap" | "wrap" | "wrap-reverse"} flexWrap
 * @property {Number} opacity
 * @property {Boolean} colorTransition
 * @property {Boolean} sizeTransition
 * @property {Boolean} positionTransition
 * @property {Boolean} allTransition
 * @property {Number} transitionDuration
 * @property {Boolean} contentEditable
 * @property {Number | String} lockWidth
 * @property {Number | String} lockHeight
 * @property {"auto" | "default" | "none" 
 * | "context-menu" | "help" | "pointer" | "progress" | "wait"
 * | "cell" | "crosshair" | "text" | "vertical-text" | "alias" 
 * | "copy" | "move" | "no-drop" | "not-allowed" | "grab" | "grabbing"
 * | "all-scroll" | "col-resize" | "row-resize" | "n-resize" | "e-resize"
 * | "s-resize" | "w-resize" | "ne-resize" | "nw-resize" | "se-resize"
 * | "sw-resize" | "ew-resize" | "ns-resize" | "nesw-resize" | "nwse-resize"
 * | "zoom-in" | "zoom-out"} cursor
 * @property {Boolean} userSelectNone
 * @property {Number | String} top
 * @property {Number | String} left
 * @property {Number | String} right
 * @property {Number | String} bottom
 * @property {Boolean} noTouchAction
 * @property {Boolean} overflowScroll
 * 
 * @typedef {Object} nativeDivParams
 * @property {String} className
 * @property {Function} onMouseEnter
 * @property {Function} onMouseLeave
 * @property {Function} onMouseDown
 * @property {Function} onMouseUp
 * @property {Function} onMouseMove
 * @property {Function} onClick
 * @property {Function} onDoubleClick
 * @property {Function} onContextMenu
 * @property {Function} onDragStart
 * @property {Function} onDragEnd
 * @property {Function} onDragOver
 * @property {Function} onDragEnter
 * @property {Function} onDragLeave
 * @property {Function} onDrop
 * @property {Boolean} draggable
 * @property {Number} blur
 * @property {Number} blurBrightness
 * @property {Number} blurOpacity
 * 
 * @typedef {xdivparamsAddOns & nativeDivParams} xdivparams
 * 
 * @param {xdivparams} param0
 */
function XDiv({
  style = {}, 
  children = null, 
  width = "100%",
  height = "auto",
  minWidth = undefined,
  maxWidth = undefined,
  minHeight = undefined,
  maxHeight = undefined, 
  flexDirection = undefined,
  justifyContent = "center", 
  alignItems = "center", 
  alignSelf = undefined,
  p = undefined,
  pH = undefined,
  pV = undefined,
  pT = undefined,
  pB = undefined,
  pL = undefined,
  pR = undefined,
  m = undefined,
  mH = undefined,
  mV = undefined,
  mT = undefined,
  mB = undefined,
  mL = undefined,
  mR = undefined,
  bW = undefined,
  bStyle = "solid",
  round = undefined,
  color = undefined,
  bColor = undefined,
  bgColor = undefined,
  shadow = undefined,
  shadowSize = 1,
  shadowColor = ColorX.CSS("greyOut", 0.2),
  fullWidth = undefined,
  fullHeight = undefined,
  fitWidthToContent = undefined,
  fitHeightToContent = undefined,
  autoWidth = undefined,
  autoHeight = undefined,
  noPropagation = false,
  forwardRef = null, 
  position = undefined,
  overflow = "hidden",
  display = "block",
  zIndex = undefined,
  flex = undefined,
  flexShrink = undefined,
  flexGrow = undefined,
  flexWrap = undefined,
  flexBasis = undefined,
  opacity = undefined,
  colorTransition = false,
  sizeTransition = false,
  positionTransition = false,
  allTransition = false,
  transitionDuration = 500,
  contentEditable = false,
  lockWidth = undefined,
  lockHeight = undefined,
  cursor = undefined,
  userSelectNone = false,
  top = undefined,
  left = undefined,
  right = undefined,
  bottom = undefined,
  noTouchAction = false,
  overflowScroll = false,
  blur = undefined,
  blurBrightness = 1,
  blurOpacity = 1,
  ...others}){

  //Quick Access
  let _addStyle = {};
  _addStyle.boxSizing = "border-box";
  if(width !== undefined) _addStyle.width = width;
  if(height !== undefined) _addStyle.height = height;
  if(flexDirection) _addStyle.flexDirection = flexDirection;
  if(justifyContent) _addStyle.justifyContent = justifyContent;
  if(alignItems) _addStyle.alignItems = alignItems;
  if(alignSelf) _addStyle.alignSelf = alignSelf;
  if(bgColor !== undefined) _addStyle.backgroundColor = bgColor;
  if(bColor !== undefined) {
    if(_.isArray(bColor)){
      _addStyle.borderTopColor = bColor[0] ?? "transparent";
      _addStyle.borderRightColor = bColor[1] ?? "transparent";
      _addStyle.borderBottomColor = bColor[2] ?? "transparent";
      _addStyle.borderLeftColor = bColor[3] ?? "transparent";
    }else{
      _addStyle.borderColor = bColor;
    }
  }
  if(color !== undefined) _addStyle.color = color;
  if(p !== undefined){
    if(_.isArray(p)){
      _addStyle.paddingTop = p[0] ?? 0;
      _addStyle.paddingRight = p[1] ?? 0;
      _addStyle.paddingBottom = p[2] ?? 0;
      _addStyle.paddingLeft = p[3] ?? 0;
    }else{
      _addStyle.padding = p;
    }
  }
  if(pH !== undefined) _addStyle.paddingLeft = _addStyle.paddingRight = pH;
  if(pV !== undefined) _addStyle.paddingTop = _addStyle.paddingBottom = pV;
  if(pT !== undefined) _addStyle.paddingTop = pT;
  if(pB !== undefined) _addStyle.paddingBottom = pB;
  if(pL !== undefined) _addStyle.paddingLeft = pL;
  if(pR !== undefined) _addStyle.paddingRight = pR;
  if(m !== undefined){
    if(_.isArray(m)){
      _addStyle.marginTop = m[0] ?? 0;
      _addStyle.marginRight = m[1] ?? 0;
      _addStyle.marginBottom = m[2] ?? 0;
      _addStyle.marginLeft = m[3] ?? 0;
    }else{
      _addStyle.margin = m;
    }
  } 
  if(mH !== undefined) _addStyle.marginLeft = _addStyle.marginRight = mH;
  if(mV !== undefined) _addStyle.marginTop = _addStyle.marginBottom = mV;
  if(mT !== undefined) _addStyle.marginTop = mT;
  if(mB !== undefined) _addStyle.marginBottom = mB;
  if(mL !== undefined) _addStyle.marginLeft = mL;
  if(mR !== undefined) _addStyle.marginRight = mR;
  if(bW !== undefined) {
    if(_.isArray(bW)){
      _addStyle.borderTopWidth  = bW[0] ?? 0;
      if(bW[0] > 0) _addStyle.borderTopStyle = _.isArray(bStyle) ? bStyle[0] : bStyle;
      _addStyle.borderRightWidth = bW[1] ?? 0;
      if(bW[1] > 0) _addStyle.borderRightStyle = _.isArray(bStyle) ? bStyle[1] : bStyle;
      _addStyle.borderBottomWidth = bW[2] ?? 0;
      if(bW[2] > 0) _addStyle.borderBottomStyle = _.isArray(bStyle) ? bStyle[2] : bStyle;
      _addStyle.borderLeftWidth = bW[3] ?? 0;
      if(bW[3] > 0) _addStyle.borderLeftStyle = _.isArray(bStyle) ? bStyle[3] : bStyle;
    }else{
      _addStyle.borderWidth = bW;
      if(bW > 0) {
        if(_.isArray(bStyle)){
          _addStyle.borderTopStyle = bStyle[0];
          _addStyle.borderRightStyle = bStyle[1];
          _addStyle.borderBottomStyle = bStyle[2];
          _addStyle.borderLeftStyle = bStyle[3];
        }else{
          _addStyle.borderStyle = bStyle;
        }
      }
    }
  }
  if(round !== undefined) {
    if(_.isArray(round)){
      _addStyle.borderTopLeftRadius = round[0] ?? 0;
      _addStyle.borderTopRightRadius = round[1] ?? 0;
      _addStyle.borderBottomLeftRadius = round[2] ?? 0;
      _addStyle.borderBottomRightRadius = round[3] ?? 0;
    }else{
      _addStyle.borderRadius = round;
    }
  }
  if(shadow !== undefined) _addStyle.boxShadow = `1px 1px ${shadowSize}px ${shadowSize}px ${shadowColor}`;
  if(fullWidth) _addStyle.width = "100%";
  if(fullHeight) _addStyle.height = "100%";
  if(fitWidthToContent) _addStyle.width = "fit-content";
  if(fitHeightToContent) _addStyle.height = "fit-content";
  if(autoWidth) _addStyle.width = "auto";
  if(autoHeight) _addStyle.height = "auto";
  if(position !== undefined) _addStyle.position = position;
  if(overflow !== undefined) _addStyle.overflow = overflow;
  if(display !== undefined) _addStyle.display = display;
  if(zIndex !== undefined) _addStyle.zIndex = zIndex;
  if(flex){
    _addStyle.display = "flex";
    _addStyle.flexGrow = 1;
    _addStyle.flexShrink = 1;
  }
  if(flexShrink !== undefined) _addStyle.flexShrink = flexShrink;
  if(flexGrow) _addStyle.flexGrow = flexGrow;
  if(flexWrap !== undefined) _addStyle.flexWrap = flexWrap;
  if(flexBasis !== undefined) _addStyle.flexBasis = flexBasis;
  if(opacity !== undefined) _addStyle.opacity = opacity;
  if(minWidth !== undefined) _addStyle.minWidth = minWidth;
  if(maxWidth !== undefined) _addStyle.maxWidth = maxWidth;
  if(minHeight !== undefined) _addStyle.minHeight = minHeight;
  if(maxHeight !== undefined) _addStyle.maxHeight = maxHeight;
  if(cursor !== undefined) _addStyle.cursor = cursor;
  if(userSelectNone) _addStyle.userSelect = "none";
  if(noTouchAction) {
    _addStyle.touchAction = "none";
  }else{
    _addStyle.touchAction = "auto";
  }

  if(top !== undefined) _addStyle.top = top;
  if(left !== undefined) _addStyle.left = left;
  if(right !== undefined) _addStyle.right = right;
  if(bottom !== undefined) _addStyle.bottom = bottom;
  
  if(lockHeight !== undefined){
    _addStyle.height = lockHeight;
    _addStyle.minHeight = lockHeight;
    _addStyle.maxHeight = lockHeight;
  }

  if(lockWidth !== undefined){
    _addStyle.width = lockWidth;
    _addStyle.minWidth = lockWidth;
    _addStyle.maxWidth = lockWidth;
  }

  let transition = style?.transition?.split(",") ?? [];
  if(colorTransition){
    transition.push(`background-color ${transitionDuration}ms`);
    transition.push(`color ${transitionDuration}ms`);
    transition.push(`border-color ${transitionDuration}ms`);
    transition.push(`opacity ${transitionDuration}ms`);
  }else{
    if(color?.day && color?.night){
      transition.push(`color ${transitionDuration}ms`);
    }
    if(bgColor?.day && bgColor?.night){
      transition.push(`background-color ${transitionDuration}ms`);
    }
    if(bColor?.day && bColor?.night){
      transition.push(`border-color ${transitionDuration}ms`);
    }
  }
  if(positionTransition){
    transition.push(`top ${transitionDuration}ms`);
    transition.push(`left ${transitionDuration}ms`);
    transition.push(`right ${transitionDuration}ms`);
    transition.push(`bottom ${transitionDuration}ms`);
  }
  if(sizeTransition){
    transition.push(`width ${transitionDuration}ms`);
    transition.push(`height ${transitionDuration}ms`);
    transition.push(`min-width ${transitionDuration}ms`);
    transition.push(`min-height ${transitionDuration}ms`);
    transition.push(`max-width ${transitionDuration}ms`);
    transition.push(`max-height ${transitionDuration}ms`);
  }
  if(allTransition){
    transition.push(`all ${transitionDuration}ms`);
  }
  if(transition?.length > 0){
    _addStyle.transition = transition.join(", ");
  }

  if(blur !== undefined){
    _addStyle.backdropFilter = `blur(${blur}px) brightness(${blurBrightness}) opacity(${blurOpacity})`;
  }

  let _onClick = noPropagation ?
    (e) => e.stopPropagation() : undefined;

  let _style = {
    ..._addStyle,
    ...style
  };

  others = XProtect.NativeProps(others);

  return (
    <div 
      style={_style} 
      ref={forwardRef} 
      onClick={_onClick}
      contentEditable={contentEditable ? true : undefined}
      {...others}>
      {children}
    </div>
  );
}

export default XDiv;